import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '../Image';
import RichText from '../RichText';
import Em from './components/Em';
import Subtitle from './components/Subtitle';
import Title from './components/Title';

interface HeroProps {
  readonly title?: MessageKey;
  readonly subtitle?: MessageKey;
  readonly imgSrc: string;
  readonly imgAlt: MessageKey;
  readonly className?: string;
  readonly copyClassNames?: string;
  readonly useFullHeight?: boolean;
}

const tValues = {
  i: (chunks: ReactNode) => <Em>{chunks}</Em>,
};

const LandingPageHero = ({
  title,
  subtitle,
  imgSrc,
  imgAlt,
  className,
  copyClassNames,
  useFullHeight = true,
}: HeroProps) => {
  const t = useTranslations();
  return (
    <section
      className={cx(
        'flex flex-col justify-between gap-10',
        useFullHeight ? 'md:h-screen lg:h-auto lg:min-h-screen' : '',
        'md:pb-24',
        'text-sandstone-500',
        className,
      )}
      id="hero"
    >
      <LayoutGrid className="gap-y-2 md:gap-y-5">
        {title && (
          <div
            className={cx('col-main', 'md:col-start-6 md:col-end-22')}
            data-cy="hero-title"
          >
            <Title h1>
              <RichText messageKey={title} tValues={tValues} />
            </Title>
          </div>
        )}

        {subtitle && (
          <div
            className={cx(
              'col-start-4 col-end-main row-start-2',
              'md:col-start-10 md:col-end-22',
              copyClassNames,
            )}
            data-cy="hero-subtitle"
          >
            <Subtitle small>{t(subtitle)}</Subtitle>
          </div>
        )}
      </LayoutGrid>

      <div
        className={cx(
          'self-end overflow-hidden',
          'w-full pt-5 md:w-10/12',
          'flex items-center',
        )}
      >
        <Image
          data-cy="hero-img"
          priority
          src={imgSrc}
          alt={t(imgAlt)}
          width={1600}
          height={800}
        />
      </div>
    </section>
  );
};

export default LandingPageHero;
